import dayjs, { Dayjs } from 'dayjs'
import { ChargeForecast } from '@/types'
import { getAxisLabels } from './UpcomingRatesCard.utils'
import {
  formatLevel,
  getLevelAxisLabels,
  LevelFormat,
  PERCENT_LEVEL,
} from './ChargeLevel.utils'
import {
  getPeriodMetric,
  getSelectedMetricKey,
} from './ChargeSessionDetails.utils'

// A charge counts as reaching the limit within 2 percentage points, matching
// batteryPercentHasReachedTarget in the charge forecast utils
const BATTERY_TARGET_PADDING_PERCENT = 2

// x runs 0..1 across the forecast window; percent is the battery level
export type ChartPoint = { x: number; percent: number }

export type ChartLine = { charging: boolean; points: ChartPoint[] }

export type ChartStripPiece = {
  charging: boolean
  startX: number
  endX: number
}

export type ChargeChart = {
  lines: ChartLine[]
  strip: ChartStripPiece[]
  startPercent: number
  startLabel: string
  targetPercent: number
  targetLabel: string | null
  // False for a charger, whose "target" is an assumption rather than a level
  showTarget: boolean
  yLabels: string[]
  targetX: number | null
  xLabels: string[]
  nowX: number | null
  // The current time, read out with the chart since the marker is visual only
  nowLabel: string | null
}

function clampFraction(value: number) {
  return Math.min(Math.max(value, 0), 1)
}

/**
 * Battery forecast chart geometry: one line per run of charging / not
 * charging periods, a strip marking the charging windows, and where the
 * battery reaches the charge limit.
 */
export function getChargeChart(args: {
  chargeForecast: ChargeForecast
  now: Dayjs
  chargeLimitPercent: number
  level?: LevelFormat
}): ChargeChart | null {
  const {
    chargeForecast,
    now,
    chargeLimitPercent,
    level = PERCENT_LEVEL,
  } = args
  const periods = chargeForecast.charge_forecast_periods ?? []

  if (!periods.length) {
    return null
  }

  const metricKey = getSelectedMetricKey(chargeForecast.selected_plan)
  const windowStart = dayjs(periods[0].start_datetime)
  const windowEnd = dayjs(periods[periods.length - 1].end_datetime)
  const span = Math.max(windowEnd.diff(windowStart), 1)
  const toX = (date: Dayjs) => clampFraction(date.diff(windowStart) / span)

  const lines = periods.reduce<ChartLine[]>((merged, period) => {
    const startMetric = getPeriodMetric(period, 'start', metricKey)
    const endMetric = getPeriodMetric(period, 'end', metricKey)
    const charging = startMetric?.should_charge === true
    const startPoint = {
      x: toX(dayjs(period.start_datetime)),
      percent: startMetric?.battery_percent ?? 0,
    }
    const endPoint = {
      x: toX(dayjs(period.end_datetime)),
      percent: endMetric?.battery_percent ?? startPoint.percent,
    }
    const previous = merged[merged.length - 1]

    if (previous && previous.charging === charging) {
      return [
        ...merged.slice(0, -1),
        { ...previous, points: [...previous.points, endPoint] },
      ]
    }

    // Start each new run where the last one ended so the line is continuous
    const origin = previous
      ? previous.points[previous.points.length - 1]
      : startPoint
    return [...merged, { charging, points: [origin, endPoint] }]
  }, [])

  const strip = lines.map((line) => ({
    charging: line.charging,
    startX: line.points[0].x,
    endX: line.points[line.points.length - 1].x,
  }))

  const targetPeriod = periods.find(
    (period) =>
      (getPeriodMetric(period, 'end', metricKey)?.battery_percent ?? 0) >=
      chargeLimitPercent - BATTERY_TARGET_PADDING_PERCENT,
  )
  const targetTime = targetPeriod ? dayjs(targetPeriod.end_datetime) : null

  return {
    lines,
    strip,
    startPercent: Math.round(lines[0].points[0].percent),
    startLabel: formatLevel(lines[0].points[0].percent, level),
    targetPercent: chargeLimitPercent,
    targetLabel:
      targetTime && level.unit !== 'none'
        ? `${formatLevel(chargeLimitPercent, level)} at ${targetTime.format(
            'hA',
          )}`
        : null,
    showTarget: level.unit !== 'none',
    yLabels: getLevelAxisLabels(level),
    targetX: targetTime ? toX(targetTime) : null,
    xLabels: getAxisLabels(windowStart, windowEnd),
    nowX: now.isAfter(windowStart) && now.isBefore(windowEnd) ? toX(now) : null,
    nowLabel:
      now.isAfter(windowStart) && now.isBefore(windowEnd)
        ? now.format('h:mm A')
        : null,
  }
}
